import { useHandler } from "@redotech/react-util/hook";
import {
  ActivityCountType,
  isConstantActivityCount,
  isConstantActivityCountType,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo } from "react";
import * as segmentBuilderCss from "../segment-builder.module.css";
import { ActivityCountInProgress } from "../segment-in-progress";

export const ActivityCountSelector = memo(function ActivityCountSelector({
  activityCount,
  setActivityCount,
}: {
  activityCount: ActivityCountInProgress;
  setActivityCount: (activityCount: ActivityCountInProgress) => void;
}) {
  const handleConditionTypeChange = useHandler(
    (activityCountType: ActivityCountType) => {
      if (activityCountType === activityCount.type) {
        return;
      }
      if (isConstantActivityCountType(activityCountType)) {
        setActivityCount({ type: activityCountType });
      } else {
        setActivityCount({ type: activityCountType, n: undefined });
      }
    },
  );

  return (
    <>
      <ActivityCountTypeSelector
        activityCountType={activityCount.type}
        setActivityCountType={handleConditionTypeChange}
      />
      {isConstantActivityCount(activityCount) ? null : (
        <ActivityCountNSelector
          setValue={(newValue) => {
            setActivityCount({ ...activityCount, n: newValue });
          }}
          value={activityCount.n}
        />
      )}
    </>
  );
});

const activityCountTypeToFriendly: Record<ActivityCountType, string> = {
  [ActivityCountType.AT_LEAST_ONCE]: "at least once",
  [ActivityCountType.ZERO_TIMES]: "zero times",
  [ActivityCountType.N_TIMES]: "equals",
  [ActivityCountType.NOT_N_TIMES]: "does not equal",
  [ActivityCountType.AT_LEAST_N]: "is at least",
  [ActivityCountType.GREATER_THAN_N]: "is greater than",
  [ActivityCountType.LESS_THAN_N]: "is less than",
  [ActivityCountType.AT_MOST_N]: "is at most",
};

const activityCountTypeOptions: RedoListItem<ActivityCountType>[] =
  Object.values(ActivityCountType).map((type) => ({
    id: type,
    type: "text",
    text: activityCountTypeToFriendly[type],
    value: type,
  }));

const ActivityCountTypeSelector = memo(function ActivityCountTypeSelector({
  activityCountType,
  setActivityCountType,
}: {
  activityCountType: ActivityCountType;
  setActivityCountType: (conditionType: ActivityCountType) => void;
}) {
  return (
    <RedoSingleSelectDropdownInput<ActivityCountType>
      fitToAnchor={false}
      flexGrow={false}
      options={activityCountTypeOptions}
      optionSelected={({ item }) => setActivityCountType(item.value)}
      selectedItem={activityCountTypeOptions.find(
        (o) => o.id === activityCountType,
      )}
      size="xs"
    />
  );
});

const ActivityCountNSelector = memo(function ActivityCountNSelector({
  value,
  setValue,
}: {
  value: number | undefined;
  setValue: (value: number | undefined) => void;
}) {
  const setNumber = useHandler((value: string) => {
    const numericValue = parseInt(value);
    if (isNaN(numericValue)) {
      setValue(undefined);
    } else {
      setValue(numericValue);
    }
  });
  return (
    <RedoTextInput
      className={segmentBuilderCss.relativeUnitValueSelector}
      constrainToInputLength
      htmlPattern="\d*"
      maxLength={2}
      setValue={setNumber}
      value={`${value ?? ""}`}
    />
  );
});
